import React, { useState, useEffect } from 'react';
import { observationApi, transportLegApi } from '../api/client';

// Records a price you looked up by hand in price_snapshot.
//
// Replaced the old form, which wrote to train_trip_offer. That table is gone:
// what is entered here now ends up in the same series as RateHawk and the
// Excel import, and so counts in the price history.
//
// Two fields that are easy to skip and that carry the whole model. "Opgezocht
// op" is not the same as the travel date: the difference between them is the
// horizon, and that is what f_horizon rests on. And "uitverkocht" is a valid
// outcome, not a failure: without that row you do not know later whether you
// searched.

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  transport_leg_id: '',
  travel_date: '',
  status: 'available',
  price: '',
  currency: 'EUR',
  fx_rate_to_eur: '',
  persons: 2,
  queried_on: today(),
  fare_class: '',
  fare_type: '',
};

function ObservationForm({ onSaved }) {
  const [legs, setLegs] = useState([]);
  const [fields, setFields] = useState(EMPTY);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    transportLegApi
      .list()
      .then((r) => setLegs(Array.isArray(r.data) ? r.data : []))
      .catch(() =>
        setMessage({ type: 'error', text: 'De reisonderdelen konden niet opgehaald worden.' })
      );
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
  };

  const noPrice = fields.status !== 'available';
  // A price in another currency needs its rate to euro, as from every source:
  // without it the price can no longer be compared with the rest of the
  // history. The field only shows when it is needed.
  const needsRate = !noPrice && fields.currency.trim().toUpperCase() !== 'EUR';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    const payload = {
      transport_leg_id: Number(fields.transport_leg_id),
      travel_date: fields.travel_date,
      status: fields.status,
      persons: Number(fields.persons),
      fare_class: fields.fare_class || null,
      fare_type: fields.fare_type || null,
      // Midnight UTC of the day it was looked up. The hour does not matter,
      // the day does: it determines the horizon.
      queried_at: fields.queried_on ? `${fields.queried_on}T00:00:00Z` : null,
    };
    if (!noPrice) {
      payload.price = fields.price;
      payload.currency = fields.currency;
      if (needsRate) payload.fx_rate_to_eur = fields.fx_rate_to_eur;
    }

    try {
      const r = await observationApi.create(payload);
      setMessage({
        type: 'success',
        text: `Bewaard als waarneming ${r.data.id} op ${r.data.transport_leg_slug}.`,
      });
      // The chosen leg and lookup date stay: you usually enter several in a
      // row for the same leg.
      setFields((prev) => ({ ...EMPTY, transport_leg_id: prev.transport_leg_id, queried_on: prev.queried_on }));
      if (onSaved) onSaved();
    } catch (error) {
      const detail = error.response && error.response.data && error.response.data.detail;
      setMessage({ type: 'error', text: detail || 'Bewaren is niet gelukt.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2>Waarneming toevoegen</h2>
      <p className="intro">
        Een prijs die je zelf opzocht. Dit komt in dezelfde tabel als wat RateHawk
        en de prijs-Excels opleveren, dus je ziet het meteen terug op het
        prijsverloop.
      </p>

      {message && <div className={`message ${message.type}`}>{message.text}</div>}

      <form onSubmit={handleSubmit}>
        <div className="filter-bar">
          <label>
            Reisonderdeel
            <select
              name="transport_leg_id"
              value={fields.transport_leg_id}
              onChange={handleChange}
              required
            >
              <option value="">kies een onderdeel</option>
              {legs.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.slug}
                </option>
              ))}
            </select>
          </label>

          <label>
            Reisdatum
            <input
              type="date"
              name="travel_date"
              value={fields.travel_date}
              onChange={handleChange}
              required
            />
          </label>

          <label>
            Opgezocht op
            <input
              type="date"
              name="queried_on"
              value={fields.queried_on}
              onChange={handleChange}
              required
            />
          </label>

          <label>
            Uitkomst
            <select name="status" value={fields.status} onChange={handleChange}>
              <option value="available">prijs gevonden</option>
              <option value="sold_out">uitverkocht</option>
              <option value="no_result">geen resultaat</option>
            </select>
          </label>
        </div>

        <div className="filter-bar">
          <label>
            Prijs voor het geheel
            <input
              type="number"
              step="0.01"
              min="0.01"
              name="price"
              value={fields.price}
              onChange={handleChange}
              disabled={noPrice}
              required={!noPrice}
              placeholder={noPrice ? 'niet van toepassing' : ''}
            />
          </label>

          <label>
            Valuta
            <input
              type="text"
              name="currency"
              maxLength={3}
              value={fields.currency}
              onChange={handleChange}
              disabled={noPrice}
            />
          </label>

          {needsRate && (
            <label>
              Koers naar EUR
              <input
                type="number"
                step="0.000001"
                min="0.000001"
                name="fx_rate_to_eur"
                value={fields.fx_rate_to_eur}
                onChange={handleChange}
                required
                placeholder={`1 ${fields.currency.trim().toUpperCase()} = ? EUR`}
              />
            </label>
          )}

          <label>
            Aantal personen
            <input
              type="number"
              min="1"
              name="persons"
              value={fields.persons}
              onChange={handleChange}
              required
            />
          </label>

          <label>
            Klasse
            <input
              type="text"
              name="fare_class"
              value={fields.fare_class}
              onChange={handleChange}
              placeholder="2e klas"
            />
          </label>

          <label>
            Tariefsoort
            <input
              type="text"
              name="fare_type"
              value={fields.fare_type}
              onChange={handleChange}
              placeholder="non-flex"
            />
          </label>
        </div>

        <p className="intro">
          De prijs is het totaal voor het aantal personen, niet per persoon. Zo
          komen de handmatige waarnemingen op dezelfde voet te staan als die van
          RateHawk, dat ook totalen geeft.
        </p>

        <button type="submit" disabled={saving}>
          {saving ? 'Bezig' : 'Bewaren'}
        </button>
      </form>
    </div>
  );
}

export default ObservationForm;
